import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { TeamCard } from '@/components/business';
import { ELOChart } from '@/components/charts';
import { UnifiedTable } from '@/components/business/UnifiedTable';
import { getTeamById, getTeamMembers } from '@/utils/dataLoader';
import type { Player, Team, HistoricalPlayer } from '@/types';

const TeamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeamData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const teamId = parseInt(id);
        if (isNaN(teamId)) {
          setLoading(false);
          return;
        }

        const [teamData, membersData] = await Promise.all([
          getTeamById(teamId),
          getTeamMembers(teamId),
        ]);

        setTeam(teamData);
        setTeamMembers(membersData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载战队数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [id]);

  if (loading) {
    return (
      <Layout currentPath={`/team/${id}`}>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-text-secondary'>加载中...</div>
        </div>
      </Layout>
    );
  }

  // 如果找不到战队，显示404
  if (!id || !team) {
    return (
      <Layout currentPath={`/team/${id}`}>
        <div className='text-center py-16'>
          <h1 className='text-2xl font-bold text-text-primary mb-4'>
            战队未找到
          </h1>
          <p className='text-text-secondary mb-6'>
            抱歉，我们找不到 ID 为 "{id}" 的战队信息
          </p>
          <button
            onClick={() => navigate(-1)}
            className='bg-glory-gold hover:bg-glory-gold-dark text-white px-6 py-2 rounded-lg transition-colors'
          >
            返回上一页
          </button>
        </div>
      </Layout>
    );
  }

  // 准备ELO图表数据 - 使用简化数据
  const chartData = {
    labels: [
      '6个月前',
      '5个月前',
      '4个月前',
      '3个月前',
      '2个月前',
      '1个月前',
      '现在',
    ],
    datasets: [
      {
        label: `${team.chineseName || team.englishName} 平均ELO变化`,
        data: [
          Math.max(1000, (team.averageElo || 1200) - 120),
          Math.max(1000, (team.averageElo || 1200) - 100),
          Math.max(1000, (team.averageElo || 1200) - 70),
          Math.max(1000, (team.averageElo || 1200) - 50),
          Math.max(1000, (team.averageElo || 1200) - 20),
          Math.max(1000, (team.averageElo || 1200) - 10),
          team.averageElo || 1200,
        ],
        borderColor: 'rgb(201, 170, 113)',
        backgroundColor: 'rgba(201, 170, 113, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // 处理选手行点击
  const handlePlayerClick = (record: Player | Team | HistoricalPlayer) => {
    const player = record as Player;
    navigate(`/player/${player.id}`);
  };

  return (
    <Layout currentPath={`/team/${id}`}>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate(-1)}
            className='text-text-secondary hover:text-text-primary transition-colors cursor-pointer'
          >
            ← 返回
          </button>
          <h1 className='text-2xl font-bold text-text-primary'>
            {team.chineseName || team.englishName} 战队详情
          </h1>
        </div>

        {/* 战队信息卡片 */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          <div className='lg:col-span-1'>
            <TeamCard team={team} className='h-fit' onClick={() => {}} />
          </div>

          {/* 统计信息概览 */}
          <div className='lg:col-span-2'>
            <div className='bg-background-secondary rounded-xl p-6 border border-border'>
              <h3 className='text-lg font-semibold text-text-primary mb-4 flex items-center gap-2'>
                <span className='w-1 h-5 bg-glory-gold rounded'></span>
                战队统计
              </h3>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-text-primary'>
                    {team.averageElo?.toLocaleString() || '计算中'}
                  </div>
                  <div className='text-sm text-text-secondary'>平均ELO</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-glory-gold'>
                    #
                    {teamMembers.length > 0
                      ? Math.ceil(
                          teamMembers.reduce(
                            (sum, p) => sum + p.currentRank,
                            0
                          ) / teamMembers.length
                        )
                      : 'N/A'}
                  </div>
                  <div className='text-sm text-text-secondary'>战队排名</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-text-primary'>
                    {teamMembers.length}
                  </div>
                  <div className='text-sm text-text-secondary'>队员数量</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-positive'>
                    {teamMembers.filter(p => p.status === 'Active').length}
                  </div>
                  <div className='text-sm text-text-secondary'>活跃队员</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-text-primary'>
                    {Math.round(
                      teamMembers.reduce((sum, p) => sum + p.matchCount, 0) /
                        teamMembers.length
                    ) || 0}
                  </div>
                  <div className='text-sm text-text-secondary'>平均场数</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-text-primary'>
                    {teamMembers.reduce((sum, p) => sum + p.mvpCount, 0)}
                  </div>
                  <div className='text-sm text-text-secondary'>总MVP次数</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-glory-gold'>
                    {Math.max(
                      ...teamMembers.map(p => p.currentElo)
                    ).toLocaleString() || 0}
                  </div>
                  <div className='text-sm text-text-secondary'>最高队员ELO</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-text-primary'>
                    {team.region}
                  </div>
                  <div className='text-sm text-text-secondary'>赛区</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ELO变化图表 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            平均ELO变化趋势
          </h2>
          <div className='bg-background-secondary rounded-xl p-6 border border-border'>
            <ELOChart data={chartData} height={400} />
          </div>
        </div>

        {/* 战队成员列表 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            战队成员
          </h2>
          <UnifiedTable
            variant='player'
            data={teamMembers}
            onRowClick={handlePlayerClick}
          />
        </div>

        {/* 战队资料 */}
        <div className='bg-background-secondary rounded-xl p-6 border border-border'>
          <h3 className='text-lg font-semibold text-text-primary mb-4 flex items-center gap-2'>
            <span className='w-1 h-5 bg-glory-gold rounded'></span>
            战队资料
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm'>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>英文名称:</span>
              <span className='text-text-primary font-medium'>
                {team.englishName}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>中文名称:</span>
              <span className='text-text-primary font-medium'>
                {team.chineseName || '暂无'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>所在赛区:</span>
              <span className='text-text-primary font-medium'>
                {team.region}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>战队状态:</span>
              <span
                className={`font-medium ${
                  team.isActive ? 'text-positive' : 'text-negative'
                }`}
              >
                {team.isActive ? '活跃' : '非活跃'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>当前排名:</span>
              <span className='text-text-primary font-medium'>
                #
                {teamMembers.length > 0
                  ? Math.ceil(
                      teamMembers.reduce((sum, p) => sum + p.currentRank, 0) /
                        teamMembers.length
                    )
                  : 'N/A'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>平均ELO:</span>
              <span className='text-text-primary font-medium'>
                {team.averageElo?.toLocaleString() || '计算中'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>队员数量:</span>
              <span className='text-text-primary font-medium'>
                {teamMembers.length}人
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>活跃队员:</span>
              <span className='text-text-primary font-medium'>
                {teamMembers.filter(p => p.status === 'Active').length}人
              </span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeamDetailPage;
